/* eslint-disable */

import React, { useEffect, useState, useMemo } from 'react'
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import { Container, ContentHeader, Title, ContentBody, UsersGrid, UserCard, UserCardContent, UserCardInfo, UserCardActions, SearchContainer, PaginationContainer, PaginationInfo, PaginationControls, PageSizeControl } from './UnblockUsers.styled';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import PageContainer from '../../components/PageContainer/PageContainer';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import {  selectMessage, selectUnblockUsers, selectPageSize, selectRowCount, selectSeverity, selectShouldOpen, setOpen, setPageSize, selectPage, setPage, fetchUnblockUsers, approveUnblockRequest, declineUnblockRequest } from './slice';
import { UnblockUser } from './types';


const UnblockUsers: React.FC = () => {
  const dispatch = useAppDispatch();
  const rowCount = useAppSelector(selectRowCount);
  const pageSize = useAppSelector(selectPageSize);
  const openServer = useAppSelector(selectShouldOpen);
  const currPage = useAppSelector(selectPage);
  const unblockUsersFromServer = useAppSelector(selectUnblockUsers);
  const [unblockUsers, setUnblockUsers] = useState(unblockUsersFromServer);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    dispatch(fetchUnblockUsers(0));
  }, [dispatch])

  useEffect(() => {
    setUnblockUsers(unblockUsersFromServer);
  }, [unblockUsersFromServer]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return unblockUsers;
    }
    const lowerQuery = searchQuery.toLowerCase();
    return unblockUsers?.filter((user: UnblockUser) =>
      (user.firstName?.toLowerCase().includes(lowerQuery) ?? false) ||
      (user.lastName?.toLowerCase().includes(lowerQuery) ?? false) ||
      (user.email?.toLowerCase().includes(lowerQuery) ?? false) ||
      (user.status?.toLowerCase().includes(lowerQuery) ?? false)
    ) ?? [];
  }, [unblockUsers, searchQuery]);

  const totalPages = Math.ceil(rowCount / pageSize);
  const startIndex = currPage * pageSize + 1;
  const endIndex = Math.min((currPage + 1) * pageSize, rowCount);
  const hasNextPage = currPage < totalPages - 1;
  const hasPreviousPage = currPage > 0;

  const handleUnblockUser = (id: string) => {
    try {
      dispatch(approveUnblockRequest(id))
      .then(() => {
        dispatch(fetchUnblockUsers(0));
      })
    } catch (e) {
      console.log(e);
    }
  }

  const handleDeclineRequest = (id: string) => {
    try {
      dispatch(declineUnblockRequest(id))
      .then(() => {
        dispatch(fetchUnblockUsers(0));
      })
    } catch (e) {
      console.log(e);
    }
  }

  const setOpenServer = (open: boolean) => {
    dispatch(setOpen(open));
  }

  const message = useAppSelector(selectMessage);
  const severity = useAppSelector(selectSeverity);

  const handlePageSizeChange = (event: any) => {
    dispatch(setPageSize(event.target.value));
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      dispatch(setPage(currPage + 1));
      dispatch(fetchUnblockUsers(currPage + 1));
    }
  };

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      dispatch(setPage(currPage - 1));
      dispatch(fetchUnblockUsers(currPage - 1));
    }
  };

  return (
      <PageContainer title='Unblock Users' open={openServer} setOpen={setOpenServer} message={message} severity={severity}>
        <Container>
          <ContentHeader>
            <Title>Unblock Users</Title>
          </ContentHeader>

          <SearchContainer>
            <TextField
              label="Search users"
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by first name, last name, email, or status"
              fullWidth
              sx={{ maxWidth: '400px' }}
            />
          </SearchContainer>

          <ContentBody>
            <UsersGrid>
              {filteredUsers.map((user: UnblockUser) => (
                <UserCard key={user.eaterId}>
                  <UserCardContent>
                    <UserCardInfo>
                      <div>
                        <strong>Name:</strong> {user.firstName} {user.lastName}
                      </div>
                      <div>
                        <strong>Email:</strong> {user.email}
                      </div>
                      <div>
                        <strong>Status:</strong> {user.status}
                      </div>
                    </UserCardInfo>
                  </UserCardContent>
                  <UserCardActions>
                    <Button variant="contained" color="primary" size="small" onClick={() => handleUnblockUser(user.eaterId)}>Approve</Button>
                    <Button variant="outlined" color="error" size="small" onClick={() => handleDeclineRequest(user.eaterId)}>Decline</Button>
                  </UserCardActions>
                </UserCard>
              ))}
            </UsersGrid>
          </ContentBody>

          {rowCount > 0 && (
            <PaginationContainer>
              <PaginationInfo>
                Showing {rowCount > 0 ? startIndex : 0} to {endIndex} of {rowCount} users
              </PaginationInfo>
              <PageSizeControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Per Page</InputLabel>
                  <Select
                    value={pageSize}
                    label="Per Page"
                    onChange={handlePageSizeChange}
                  >
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </PageSizeControl>
              <PaginationControls>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handlePreviousPage}
                  disabled={!hasPreviousPage}
                >
                  Previous
                </Button>
                <span>{currPage + 1} of {totalPages || 1}</span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleNextPage}
                  disabled={!hasNextPage}
                >
                  Next
                </Button>
              </PaginationControls>
            </PaginationContainer>
          )}
        </Container>
      </PageContainer>
  )
}

export default withAuthenticationRequired(UnblockUsers);
